/**
 * AddressList Component
 * Displays list of addresses from search results with selection functionality
 */

import React, { useState, useEffect } from 'react';
import { UI_CONFIG } from '../utils/addressConstants';

/**
 * AddressList Component
 * @param {Object} props - Component props
 * @param {Array} props.addresses - Array of address objects
 * @param {Function} props.onAddressSelect - Address selection handler
 * @param {Object} props.selectedAddress - Currently selected address
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.maxHeight - Maximum height for scrollable list
 */
const AddressList = ({
  addresses = [],
  onAddressSelect,
  selectedAddress = null,
  isLoading = false,
  className = '',
  maxHeight = 300
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  /**
   * Handle keyboard navigation
   */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!addresses.length) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(prev => 
            prev < addresses.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(prev => prev > 0 ? prev - 1 : prev);
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && addresses[focusedIndex]) {
            handleAddressClick(addresses[focusedIndex], focusedIndex);
          }
          break;
        case 'Escape':
          setFocusedIndex(-1);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [addresses, focusedIndex]);

  /**
   * Handle address selection
   */
  const handleAddressClick = (address, index) => {
    if (onAddressSelect) {
      onAddressSelect(address);
    }
    setFocusedIndex(index);
  };

  /**
   * Format address for display
   */
  const formatAddressDisplay = (address) => {
    const parts = [];
    
    if (address.line1) parts.push(address.line1);
    if (address.line2) parts.push(address.line2);
    if (address.line3) parts.push(address.line3);
    
    const mainAddress = parts.join(', ');
    const locality = [address.city, address.county].filter(Boolean).join(', ');
    
    return {
      mainAddress: mainAddress || 'Address not available',
      locality,
      postcode: address.postcode || ''
    };
  };

  /**
   * Check if address is selected
   */
  const isAddressSelected = (address) => {
    if (!selectedAddress) return false;
    return selectedAddress.uprn === address.uprn;
  };

  if (isLoading) {
    return (
      <div className={`w-full border border-gray-300 rounded-md bg-white p-4 text-center ${className}`}>
        <div className="flex items-center justify-center gap-2 text-gray-500">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" aria-hidden="true"></div>
          <span>Searching for addresses...</span>
        </div>
      </div>
    );
  }

  if (!addresses.length) {
    return null;
  }

  return (
    <div className={`w-full border border-gray-300 rounded-md bg-white shadow-md ${className}`}>
      {/* Only show header if not overridden by parent */}
      {!className.includes('border-none') && (
        <div className="px-4 py-3 border-b border-gray-300 bg-gray-50 rounded-t-md">
          <h4 className="m-0 text-sm font-semibold text-gray-700">
            {addresses.length} address{addresses.length !== 1 ? 'es' : ''} found
          </h4>
          <div className="mt-1 text-xs text-gray-500">
            Click on an address to select it
          </div>
        </div>
      )}
      
      <div 
        className={`overflow-y-auto ${!className.includes('border-none') ? 'rounded-b-md' : ''} ${maxHeight === 9999 ? 'h-full' : ''}`}
        style={maxHeight !== 9999 ? { maxHeight: `${maxHeight}px` } : {}}
        role="listbox"
        aria-label="Address search results"
      >
        {addresses.map((address, index) => {
          const { mainAddress, locality, postcode } = formatAddressDisplay(address);
          const isSelected = isAddressSelected(address);
          const isFocused = focusedIndex === index;
          const isHovered = hoveredIndex === index;
          
          return (
            <div
              key={address.uprn || `address-${index}`}
              className={`flex items-start px-4 py-3 border-b border-gray-100 cursor-pointer transition-all duration-200 relative last:border-b-0 ${
                isSelected ? 'bg-blue-50 border-l-4 border-l-blue-600 shadow-sm' : ''
              } ${
                isFocused ? 'outline outline-2 outline-blue-600 outline-offset-[-2px] bg-blue-50' : ''
              } ${
                isHovered && !isSelected && !isFocused ? 'bg-pest-teal/10 shadow-md' : ''
              }`}
              onClick={() => handleAddressClick(address, index)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(-1)}
              role="option"
              aria-selected={isSelected}
              tabIndex={isFocused ? 0 : -1}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 mb-1 break-words">
                  {mainAddress}
                </div>
                {locality && (
                  <div className="text-sm text-gray-500">
                    {locality}
                  </div>
                )}
              </div>
              
              {isSelected && (
                <div className="ml-3 text-blue-600 font-bold text-lg flex-shrink-0" aria-hidden="true">
                  ✓
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
};

export default AddressList;